import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, AlertCircle } from "lucide-react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/apiClient";
import { fmtDate } from "@/components/BookingModal";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | error
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setResult(data.result || {});
          setStatus("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch (e) { /* keep trying */ }
      if (tries++ < 12) { timer = setTimeout(poll, 2000); } else { setStatus("error"); }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <Layout page="payment-success">
      <section className="relative min-h-[80vh] flex items-center justify-center px-5">
        <div className="relative w-full max-w-lg rounded-3xl bg-ink-800/95 border border-white/12 shadow-2xl backdrop-blur-xl p-10 text-center" data-testid="payment-success">
          {status === "checking" && (
            <>
              <Loader2 className="animate-spin text-cyan mx-auto" size={40} />
              <h1 className="font-display text-2xl font-bold text-white mt-6">Confirming your payment…</h1>
              <p className="text-ice/70 mt-2">One moment while we check with Stripe.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-cyan/15 flex items-center justify-center mb-5"><Check className="text-cyan" size={30} /></div>
              {result?.type === "one_to_one" ? (
                <>
                  <h1 className="font-display text-3xl font-bold text-white">1:1 sessions secured.</h1>
                  <p className="text-ice/75 mt-3">{result.plan_name}</p>
                  <div className="mt-5 rounded-xl bg-ink-800 border border-white/12 p-4">
                    <p className="label mb-1">Your code</p>
                    <p className="font-display text-2xl text-cyan tracking-wide" data-testid="success-oto-code">{result.code}</p>
                  </div>
                  <p className="text-mist text-xs mt-4">{result.oto_credits} session credit{result.oto_credits === 1 ? "" : "s"}{result.group_credits ? ` + ${result.group_credits} group class credits` : ""}{result.expires_at ? ` · expires ${result.expires_at.slice(0, 10)}` : ""}. Alexia will be in touch to arrange your sessions. Use this code to book group classes free of charge.</p>
                </>
              ) : result?.type === "programme" ? (
                <>
                  <h1 className="font-display text-3xl font-bold text-white">Your programme is ready.</h1>
                  <p className="text-ice/75 mt-3">{result.title}</p>
                  <a href={result.programme_url} data-testid="success-programme-download" className="book-cta mt-6 mx-auto">Download PDF</a>
                  <p className="text-mist text-xs mt-4">We've also emailed you the download link, so you can open it any time.</p>
                </>
              ) : result?.type === "bundle" ? (
                <>
                  <h1 className="font-display text-3xl font-bold text-white">Bundle secured.</h1>
                  <p className="text-ice/75 mt-3">{result.bundle_name} · {result.credits} credits</p>
                  <div className="mt-5 rounded-xl bg-ink-800 border border-white/12 p-4">
                    <p className="label mb-1">Your bundle code</p>
                    <p className="font-display text-2xl text-cyan tracking-wide" data-testid="success-bundle-code">{result.code}</p>
                  </div>
                  <p className="text-mist text-xs mt-4">Enter this code when you book each class. We've emailed it to you too.</p>
                  {result.programme_url && <a href={result.programme_url} data-testid="success-programme-download" className="book-cta mt-5 mx-auto">Download your programme</a>}
                </>
              ) : (
                <>
                  <h1 className="font-display text-3xl font-bold text-white">You're booked in.</h1>
                  <p className="text-ice/75 mt-3">{result?.class_date ? fmtDate(result.class_date) : result?.day} {result?.time}</p>
                  <p className="text-ice/60 mt-1">{result?.location_name}</p>
                  <p className="mt-4 text-cyan font-medium">Paid £{result?.amount}</p>
                  {result?.access_info && <div className="mt-5 rounded-xl bg-ink-800 border border-white/12 p-4 text-left" data-testid="success-access-info"><p className="label mb-1">Getting in</p><p className="text-ice/85 text-sm whitespace-pre-line">{result.access_info}</p></div>}
                  <p className="text-mist text-xs mt-4">A confirmation email is on its way.</p>
                </>
              )}
              <Link to="/" data-testid="success-home" className="book-cta mt-8 mx-auto">Back home</Link>
            </>
          )}
          {status === "error" && (
            <>
              <div className="mx-auto h-16 w-16 rounded-full bg-red-400/15 flex items-center justify-center mb-5"><AlertCircle className="text-red-300" size={30} /></div>
              <h1 className="font-display text-2xl font-bold text-white">We couldn't confirm this yet.</h1>
              <p className="text-ice/70 mt-3">If you completed payment, your booking is safe — please check your email or contact us and we'll sort it right away.</p>
              <Link to="/book" data-testid="success-retry" className="book-cta mt-8 mx-auto">Back to booking</Link>
            </>
          )}
        </div>
      </section>
    </Layout>
  );
}
